import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import PublicNav from '../components/PublicNav';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const blogPosts = [
  {
    slug: 'how-to-win-survivor-fantasy-league',
    title: 'How to Win Your Survivor Fantasy League',
    description: 'Winning a Survivor fantasy league takes more than luck. Here\'s how to think about drafting, bonus questions, and point strategy from day one.',
    date: 'April 2026',
    readTime: '5 min read'
  },
  {
    slug: 'draft-day-strategy',
    title: 'Draft Day Strategy: Who to Pick and When',
    description: 'Snake draft strategy for Survivor Fantasy League. How to identify high-value castaways, what to avoid, and how to build a balanced roster.',
    date: 'April 2026',
    readTime: '5 min read'
  },
  {
    slug: 'anatomy-of-a-survivor-fantasy-season',
    title: 'The Anatomy of a Survivor Fantasy Season',
    description: 'A walkthrough of what a full Survivor fantasy season looks like from draft night to the finale — and where the most points are won and lost.',
    date: 'April 2026',
    readTime: '5 min read'
  }
];

export default function BlogIndexPage() {
  return (
    <div className="min-h-screen pb-24 md:pb-0" style={{ backgroundColor: '#F5F0E6' }}>
      <Helmet>
        <title>Blog | Survivor Fantasy League</title>
        <meta name="description" content="Survivor strategy, fantasy league tips, and guides to help you dominate your league from draft night to the finale." />
        <link rel="canonical" href="https://survivorfantasy.live/blog" />
        <meta property="og:title" content="Blog | Survivor Fantasy League" />
        <meta property="og:description" content="Survivor strategy, fantasy league tips, and guides to help you dominate your league from draft night to the finale." />
        <meta property="og:url" content="https://survivorfantasy.live/blog" />
      </Helmet>

      <PublicNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#FF7F50' }} />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#4682B4' }} />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Strategy. Analysis. Trash Talk.
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto" style={{ color: '#F5F0E6' }}>
            Guides and takes for Survivor fans who want an edge in their fantasy league.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <article className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid md:grid-cols-1 gap-8">
            {blogPosts.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`}>
                <Card className="bg-white border-2 hover:border-[#FF7F50] transition-all hover:shadow-xl" style={{ borderColor: '#4682B4' }}>
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center gap-4 text-sm mb-4" style={{ color: '#4682B4' }}>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {post.readTime}
                      </span>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold mb-3" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
                      {post.title}
                    </h2>
                    <p className="mb-4" style={{ color: '#4682B4' }}>
                      {post.description}
                    </p>
                    <span className="inline-flex items-center gap-1 font-medium" style={{ color: '#FF7F50' }}>
                      Read more <ArrowRight className="w-4 h-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center py-8 px-6 rounded-2xl mt-12" style={{ backgroundColor: '#1E3A4C' }}>
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ready to put these tips to work?
            </h3>
            <p className="mb-6" style={{ color: '#F5F0E6' }}>
              Create your league and start drafting.
            </p>
            <Link to="/login?tab=register">
              <Button size="lg" className="text-white px-8" style={{ backgroundColor: '#FF7F50' }}>
                Sign Up Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-12" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo-new.png" alt="Survivor Fantasy League" className="w-10 h-10" />
              <div>
                <span className="text-white font-bold text-lg">Survivor Fantasy League</span>
                <p className="text-xs" style={{ color: '#4682B4' }}>The Ultimate Survivor Experience</p>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm" style={{ color: '#F5F0E6' }}>
              <Link to="/" className="hover:text-[#FF7F50] transition-colors">Home</Link>
              <Link to="/how-it-works" className="hover:text-[#FF7F50] transition-colors">How It Works</Link>
              <Link to="/rules" className="hover:text-[#FF7F50] transition-colors">Rules</Link>
              <Link to="/faq" className="hover:text-[#FF7F50] transition-colors">FAQ</Link>
              <Link to="/about" className="hover:text-[#FF7F50] transition-colors">About</Link>
              <Link to="/blog" className="hover:text-[#FF7F50] transition-colors">Blog</Link>
              <Link to="/privacy" className="hover:text-[#FF7F50] transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-[#FF7F50] transition-colors">Terms</Link>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center text-sm" style={{ borderColor: '#4682B4', color: '#4682B4' }}>
            <p>© {new Date().getFullYear()} Survivor Fantasy League. Not affiliated with CBS or Survivor.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
